import { useEffect, useState } from "react";
import { Copy, CheckCircle2, Mail, Phone, Loader2, ShieldCheck, X } from "lucide-react";
import AppShell from "@/components/AppShell";
import { api, getErrorMessage } from "@/lib/api";

/* =================== Verify Contact Modal =================== */
export function VerifyContactModal({ type, onClose, onVerified, savedPhone = "" }) {
  const isEmail = type === "email";
  const [step, setStep] = useState(isEmail ? "send" : "enter_phone");
  const [phone, setPhone] = useState(savedPhone || "");
  const [code, setCode] = useState("");
  const [otpLength, setOtpLength] = useState(6);
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  async function sendCode() {
    setErr(""); setLoading(true);
    try {
      if (isEmail) {
        await api.post("/api/auth/email/resend-otp");
        setStep("verify");
        setCooldown(60);
      } else {
        const { data } = await api.post("/api/auth/mobile/send-otp", { mobile_number: phone });
        setOtpLength(Number.isInteger(data?.otp_length) && data.otp_length >= 4 ? data.otp_length : 6);
        setCode("");
        setStep("verify");
        setCooldown(60);
      }
    } catch (e) { setErr(getErrorMessage(e)); }
    finally { setLoading(false); }
  }

  async function verify() {
    setErr(""); setLoading(true);
    try {
      if (isEmail) {
        await api.post("/api/auth/email/verify-otp", { code });
      } else {
        await api.post("/api/auth/mobile/verify-otp", { code });
      }
      onVerified();
      onClose();
    } catch (e) { setErr(getErrorMessage(e)); }
    finally { setLoading(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-card rounded-2xl border border-border shadow-2xl max-w-[420px] w-full p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            {isEmail ? <Mail size={20} className="text-primary" /> : <Phone size={20} className="text-primary" />}
            <h3 className="text-[16px] font-semibold text-foreground">
              {isEmail ? "Verify Email" : "Verify Mobile"}
            </h3>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1">
            <X size={20} />
          </button>
        </div>

        {step === "enter_phone" && (
          <div className="space-y-4">
            <p className="text-[13px] text-muted-foreground">
              {savedPhone
                ? "We'll text a code to the number you saved at signup. You can correct it before sending."
                : "Enter your mobile number to receive a verification code."}
            </p>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/[^\d+]/g, ""))}
              placeholder="+91 98765 43210"
              className="w-full px-4 py-3 rounded-lg border border-border bg-surface-2 text-[14px] outline-none focus:border-primary "
            />
            {err && <p className="text-[12px] text-down">{err}</p>}
            <button
              onClick={sendCode}
              disabled={loading || !phone}
              className="w-full py-3 rounded-lg brand-gradient text-primary-foreground font-semibold text-[14px] disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {loading && <Loader2 size={14} className="animate-spin" />}
              Send OTP
            </button>
          </div>
        )}

        {step === "send" && isEmail && (
          <div className="space-y-4">
            <p className="text-[13px] text-muted-foreground">
              We'll send a 6-digit verification code to your registered email address.
            </p>
            {err && <p className="text-[12px] text-down">{err}</p>}
            <button
              onClick={sendCode}
              disabled={loading}
              className="w-full py-3 rounded-lg brand-gradient text-primary-foreground font-semibold text-[14px] disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {loading && <Loader2 size={14} className="animate-spin" />}
              Send verification code
            </button>
          </div>
        )}

        {step === "verify" && (
          <div className="space-y-4">
            <p className="text-[13px] text-muted-foreground">
              Enter the {isEmail ? 6 : otpLength}-digit code sent to your {isEmail ? "email" : "mobile number"}.
              {isEmail ? <> Also check your <strong>spam folder</strong>.</> : null}
            </p>
            <input
              type="text"
              inputMode="numeric"
              maxLength={isEmail ? 6 : otpLength}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              placeholder={"_".repeat(isEmail ? 6 : otpLength)}
              className="w-full px-4 py-3 rounded-lg border border-border bg-surface-2 text-[18px] font-mono text-center tracking-[0.4em] outline-none focus:border-primary "
            />
            {err && <p className="text-[12px] text-down">{err}</p>}
            <button
              onClick={verify}
              disabled={loading || code.length < (isEmail ? 6 : otpLength)}
              className="w-full py-3 rounded-lg brand-gradient text-primary-foreground font-semibold text-[14px] disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {loading && <Loader2 size={14} className="animate-spin" />}
              Verify
            </button>
            <button
              onClick={step === "verify" && !isEmail ? () => setStep("enter_phone") : sendCode}
              disabled={loading || cooldown > 0}
              className="w-full text-[13px] text-primary font-semibold hover:underline disabled:opacity-50"
            >
              {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* =================== Verification Banner =================== */
function VerificationBanner({ emailVerified, mobileVerified, onVerify }) {
  if (emailVerified && mobileVerified) return null;
  return (
    <div className="bg-warning/10 border border-warning/30 rounded-2xl p-4 space-y-3">
      <h3 className="text-[13px] font-semibold text-warning">Pending verifications</h3>
      {!emailVerified && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Mail size={15} className="text-warning" />
            <span className="text-[13px] text-warning">Email not verified</span>
          </div>
          <button
            onClick={() => onVerify("email")}
            className="text-[12px] font-semibold text-primary hover:underline px-3 py-1.5 rounded-md bg-card border border-line"
          >
            Verify now
          </button>
        </div>
      )}
      {!mobileVerified && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Phone size={15} className="text-warning" />
            <span className="text-[13px] text-warning">Mobile not verified</span>
          </div>
          <button
            onClick={() => onVerify("mobile")}
            className="text-[12px] font-semibold text-primary hover:underline px-3 py-1.5 rounded-md bg-card border border-line"
          >
            Verify now
          </button>
        </div>
      )}
    </div>
  );
}

export default function ProfilePage() {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [verifyModal, setVerifyModal] = useState(null); // "email" | "mobile" | null
  const [nameDraft, setNameDraft] = useState("");
  const [editingName, setEditingName] = useState(false);
  const [nameBusy, setNameBusy] = useState(false);
  const [nameErr, setNameErr] = useState("");
  const [nameOk, setNameOk] = useState("");

  async function loadProfile() {
    const r = await api.get("/api/user/overview");
    setData(r.data);
    const existing =
      r.data?.user?.full_name ||
      r.data?.kyc_profile?.full_name ||
      "";
    setNameDraft(existing === "-" || existing === "—" ? "" : existing);
  }

  useEffect(() => { loadProfile(); }, []);

  function handleVerified() {
    loadProfile();
  }

  const displayName =
    data?.user?.full_name ||
    data?.kyc_profile?.full_name ||
    "";
  const needsName =
    !displayName ||
    displayName === "-" ||
    displayName === "—";

  async function saveName() {
    setNameErr("");
    setNameOk("");
    const name = nameDraft.trim().replace(/\s+/g, " ");
    if (name.length < 2 || name === "-") {
      setNameErr("Please enter your full name");
      return;
    }
    setNameBusy(true);
    try {
      await api.patch("/api/auth/profile", { full_name: name });
      setEditingName(false);
      setNameOk("Name updated.");
      await loadProfile();
    } catch (e) {
      setNameErr(getErrorMessage(e));
    } finally {
      setNameBusy(false);
    }
  }

  return (
    <AppShell>
      {verifyModal && (
        <VerifyContactModal
          type={verifyModal}
          savedPhone={verifyModal === "mobile" ? data?.user?.mobile_number || "" : ""}
          onClose={() => setVerifyModal(null)}
          onVerified={handleVerified}
        />
      )}

      <div className="space-y-6">
        <h1 className="text-[26px] font-semibold text-foreground tracking-tight">Profile</h1>

        {!data ? (
          <div className="text-muted-foreground">Loading…</div>
        ) : (
          <>
            <VerificationBanner
              emailVerified={data.user?.email_verified}
              mobileVerified={data.user?.mobile_verified}
              onVerify={(type) => setVerifyModal(type)}
            />

            {needsName && !editingName ? (
              <div className="rounded-xl border border-warning/30 bg-warning/10 px-4 py-3 text-[13px] text-warning flex flex-wrap items-center justify-between gap-3">
                <span>Your profile is missing a full name. Add it so Digio KYC and Travel Rule can use the correct identity.</span>
                <button
                  type="button"
                  onClick={() => setEditingName(true)}
                  className="px-3 py-1.5 rounded-lg brand-gradient text-primary-foreground text-[12px] font-semibold"
                >
                  Add name
                </button>
              </div>
            ) : null}

            <div className="bg-card rounded-2xl border border-border p-6 space-y-5">
              <div className="grid grid-cols-2 gap-x-6 gap-y-4 text-[13px]">
                <div className="col-span-2 sm:col-span-1">
                  <div className="text-muted-foreground text-[11px] uppercase tracking-wider">Full name</div>
                  {editingName ? (
                    <div className="mt-1 space-y-2">
                      <input
                        type="text"
                        value={nameDraft}
                        onChange={(e) => setNameDraft(e.target.value)}
                        placeholder="As on your government ID"
                        className="w-full px-3 py-2 rounded-lg border border-border bg-surface-2 text-[14px] outline-none focus:border-primary"
                        data-testid="profile-full-name"
                      />
                      {nameErr ? <p className="text-[12px] text-down">{nameErr}</p> : null}
                      <div className="flex gap-2">
                        <button
                          type="button"
                          disabled={nameBusy}
                          onClick={saveName}
                          className="px-3 py-1.5 rounded-lg brand-gradient text-primary-foreground text-[12px] font-semibold disabled:opacity-60 inline-flex items-center gap-1"
                        >
                          {nameBusy ? <Loader2 size={12} className="animate-spin" /> : null}
                          Save
                        </button>
                        <button
                          type="button"
                          disabled={nameBusy}
                          onClick={() => {
                            setEditingName(false);
                            setNameErr("");
                            setNameDraft(
                              displayName === "-" || displayName === "—" ? "" : displayName
                            );
                          }}
                          className="px-3 py-1.5 rounded-lg border border-border text-[12px] font-semibold text-muted-foreground"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-foreground font-medium flex items-center gap-2 mt-0.5">
                      <span>{needsName ? "—" : displayName}</span>
                      <button
                        type="button"
                        onClick={() => setEditingName(true)}
                        className="text-[12px] font-semibold text-primary hover:underline"
                      >
                        Edit
                      </button>
                    </div>
                  )}
                  {nameOk && !editingName ? (
                    <p className="text-[12px] text-up mt-1">{nameOk}</p>
                  ) : null}
                </div>
                {[
                  ["Email", data.user.email],
                  ["Mobile", data.user.mobile_number || data.kyc_profile?.mobile_number || "—"],
                  ["Account ID", data.user.id],
                  ["Role", data.user.role || "user"],
                  ["Member since", data.user.created_at?.slice(0, 10)],
                  ["KYC status", data.user.kyc_status],
                ].map(([k, v]) => (
                  <div key={k}>
                    <div className="text-muted-foreground text-[11px] uppercase tracking-wider">{k}</div>
                    <div className="text-foreground font-medium flex items-center gap-2">
                      {v || "—"}
                      {k === "Account ID" && (
                        <button
                          onClick={async () => {
                            await navigator.clipboard.writeText(data.user.id);
                            setCopied(true);
                            setTimeout(() => setCopied(false), 1200);
                          }}
                          className="text-muted-foreground hover:text-primary"
                          aria-label="Copy account id"
                        >
                          {copied ? <CheckCircle2 size={14} /> : <Copy size={14} />}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Verification status row */}
              <div className="pt-4 border-t border-border">
                <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-3">Verification status</p>
                <div className="flex flex-wrap gap-3">
                  <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-[12px] font-semibold border ${
                    data.user?.email_verified
                      ? "bg-up/10 border-up/30 text-up"
                      : "bg-warning/10 border-warning/30 text-warning"
                  }`}>
                    <Mail size={13} />
                    Email: {data.user?.email_verified ? "Verified" : "Not verified"}
                    {data.user?.email_verified && <ShieldCheck size={12} />}
                  </div>
                  <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-[12px] font-semibold border ${
                    data.user?.mobile_verified
                      ? "bg-up/10 border-up/30 text-up"
                      : "bg-warning/10 border-warning/30 text-warning"
                  }`}>
                    <Phone size={13} />
                    Mobile: {data.user?.mobile_verified ? "Verified" : "Not verified"}
                    {data.user?.mobile_verified && <ShieldCheck size={12} />}
                  </div>

                  {!data.user?.email_verified && (
                    <button
                      onClick={() => setVerifyModal("email")}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-[12px] font-semibold brand-gradient text-primary-foreground hover:brightness-110 transition"
                    >
                      <Mail size={13} /> Verify email
                    </button>
                  )}
                  {!data.user?.mobile_verified && (
                    <button
                      onClick={() => setVerifyModal("mobile")}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-[12px] font-semibold brand-gradient text-primary-foreground hover:brightness-110 transition"
                    >
                      <Phone size={13} /> Verify mobile
                    </button>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </AppShell>
  );
}
